/** biome-ignore-all lint/correctness/useUniqueElementIds: expected */
import { useEffect, useState } from 'react';
import { RiCheckboxCircleFill, RiErrorWarningLine, RiLockPasswordLine, RiMailLine } from 'react-icons/ri';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../../components/primitives/button';
import { Input } from '../../components/primitives/input';
import { apiHostnameManager } from '../../utils/api-hostname-manager';
import { getApiHostnameCandidates } from '../../utils/local-api-fallback';
import { getJwtToken, isJwtValid, setJwtToken } from './jwt-manager';

type ClerkWindow = Window & {
  Clerk?: Record<string, unknown> & { loggedIn?: boolean };
};

const AUTH_LOADING_MIN_MS = 450;

async function withMinimumLoadingDelay(startedAt: number) {
  const elapsed = Date.now() - startedAt;
  const remaining = AUTH_LOADING_MIN_MS - elapsed;

  if (remaining > 0) {
    await new Promise((resolve) => setTimeout(resolve, remaining));
  }
}

function isSelfHostedSignedIn(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const clerkWindow = window as ClerkWindow;

  if (typeof clerkWindow.Clerk?.loggedIn === 'boolean') {
    return clerkWindow.Clerk.loggedIn && isJwtValid(getJwtToken());
  }

  return isJwtValid(getJwtToken());
}

function isAuthPath(pathname: string): boolean {
  const normalized = pathname.toLowerCase();

  return normalized.startsWith('/auth/sign-in') || normalized.startsWith('/auth/sign-up');
}

function shouldIncludeRedirectUrl(redirectUrl?: string): boolean {
  if (!redirectUrl) {
    return false;
  }

  try {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(redirectUrl, baseUrl);

    return !isAuthPath(parsed.pathname);
  } catch {
    return !isAuthPath(redirectUrl);
  }
}

async function postSelfHostedAuth(path: '/v1/auth/login' | '/v1/auth/register', payload: unknown): Promise<Response> {
  const apiCandidates = getApiHostnameCandidates(apiHostnameManager.getApiHostname());
  let lastNetworkError: unknown;

  for (const apiBase of apiCandidates) {
    try {
      const response = await fetch(`${apiBase}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok && apiBase !== apiCandidates[0]) {
        apiHostnameManager.setApiHostname(apiBase);
      }

      return response;
    } catch (error) {
      lastNetworkError = error;
    }
  }

  if (lastNetworkError instanceof Error) {
    throw lastNetworkError;
  }

  throw new Error('Unable to reach pingr-notify authentication service. Please ensure API is running on localhost:3000 or localhost:3010.');
}

export function OrganizationList() {
  return <></>;
}

export function OrganizationProfile() {
  return <></>;
}

/**
 * Self-hosted account screen.
 *
 * Renders two panels side by side: EMAIL on the left, PASSWORD on the right.
 *
 * Both are here because the community build has no Clerk/better-auth profile
 * UI. Previously this screen offered only a password change, so there was no
 * way to change the account email at all, and the only route to a reset was to
 * sign out first.
 *
 * Password  -> POST /v1/auth/update-password
 * Email     -> POST /v1/users/profile/email/change-request  (two-phase)
 *              GET  /v1/users/profile/email/change-status
 *
 * The email change is deliberately NOT immediate: it creates a pending request,
 * emails a confirmation link to the new address and a security alert (with a
 * cancel link) to the current one, and only applies when the new inbox
 * confirms. The copy below must never imply the address has already changed.
 */
export function UserProfile() {
  // ---- password ----
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // ---- email ----
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<{ newEmail: string; expiresAt: string } | null>(null);
  const [currentEmail, setCurrentEmail] = useState<string>('');

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getJwtToken() ?? ''}`,
  });

  /** Read the signed-in address straight from the JWT — no extra request. */
  useEffect(() => {
    const token = getJwtToken();

    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
      if (payload?.email) setCurrentEmail(payload.email);
    } catch {
      /* a malformed token is handled by the auth guard, not here */
    }
  }, []);

  /** Any outstanding change, so the banner survives a page reload. */
  const loadPending = async () => {
    try {
      const apiBase = apiHostnameManager.getApiHostname();
      const res = await fetch(`${apiBase}/v1/users/profile/email/change-status`, { headers: authHeaders() });

      if (!res.ok) return;

      const body = await res.json();
      const data = body?.data ?? body;
      setPending(data?.pending ? { newEmail: data.newEmail, expiresAt: data.expiresAt } : null);
    } catch {
      /* non-fatal: the form still works without the banner */
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    // Validate before the round-trip so the user gets an instant, specific message.
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the current password.');
      return;
    }

    setIsSubmitting(true);
    try {
      const apiBase = apiHostnameManager.getApiHostname();
      const response = await fetch(`${apiBase}/v1/auth/update-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });

      if (!response.ok) {
        // 204 No Content is success; anything else carries a message we should surface verbatim.
        let message = `Could not update password (${response.status}).`;
        try {
          const body = await response.json();
          const raw = body?.message ?? body?.error;
          if (typeof raw === 'string' && raw.trim()) message = raw;
          else if (Array.isArray(raw) && typeof raw[0] === 'string') message = raw[0];
        } catch {
          /* keep the status-code fallback */
        }
        setError(message);
        return;
      }

      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setError('Unable to reach the pingr-notify API. Check that it is running and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setEmailError(null);
    setEmailNotice(null);

    if (currentEmail && newEmail.trim().toLowerCase() === currentEmail.toLowerCase()) {
      setEmailError('That is already your email address.');
      return;
    }

    setIsEmailSubmitting(true);
    try {
      const apiBase = apiHostnameManager.getApiHostname();
      const response = await fetch(`${apiBase}/v1/users/profile/email/change-request`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ newEmail: newEmail.trim(), currentPassword: emailPassword }),
      });

      const body = await response.json().catch(() => null);

      if (!response.ok) {
        const raw = body?.message ?? body?.error;
        setEmailError(
          typeof raw === 'string' && raw.trim()
            ? raw
            : Array.isArray(raw) && typeof raw[0] === 'string'
              ? raw[0]
              : `Could not start the email change (${response.status}).`
        );
        return;
      }

      setEmailNotice(
        body?.data?.message ?? 'Check your new inbox for a confirmation link. Your address has not changed yet.'
      );
      setNewEmail('');
      await loadPending();
    } catch {
      setEmailError('Unable to reach the pingr-notify API. Check that it is running and try again.');
    } finally {
      // Never keep the password in state longer than the request needs it.
      setEmailPassword('');
      setIsEmailSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[880px] px-1 py-1">
      <header className="border-stroke-soft mb-5 border-b pb-4">
        <h2 className="text-label-lg text-text-strong">Account</h2>
        <p className="text-paragraph-xs text-text-soft mt-1">
          Manage the email address and password used to sign in to pingr-notify.
        </p>
      </header>

      {/*
       * Two equal-height cards. The grid stretches them, and each card pins its
       * footnote to the bottom with `mt-auto`, so the two footers stay aligned
       * even though the email side grows when a pending change is showing.
       */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* ------------------------------- EMAIL ------------------------------- */}
        <section className="border-stroke-soft bg-bg-white flex flex-col rounded-xl border">
          <div className="border-stroke-soft bg-bg-weak flex items-center gap-2 rounded-t-xl border-b px-4 py-2.5">
            <RiMailLine className="text-text-sub size-4 shrink-0" />
            <h3 className="text-label-sm text-text-strong">Email address</h3>
          </div>

          <div className="flex flex-1 flex-col gap-3.5 p-4">
            <div className="border-stroke-soft flex items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2">
              <span className="text-paragraph-2xs text-text-soft shrink-0">Signed in as</span>
              <span className="text-label-xs text-text-strong truncate" title={currentEmail}>
                {currentEmail || '—'}
              </span>
            </div>

            {pending && (
              <div className="bg-warning-lighter flex items-start gap-2 rounded-lg p-3">
                <RiErrorWarningLine className="text-warning-base mt-px size-4 shrink-0" />
                <div className="min-w-0">
                  <p className="text-label-xs text-text-strong">Waiting for confirmation</p>
                  <p className="text-paragraph-2xs text-text-sub mt-1">
                    We emailed a link to <span className="text-text-strong font-medium">{pending.newEmail}</span>. Your
                    address stays <span className="text-text-strong font-medium">{currentEmail}</span> until it is
                    confirmed.
                  </p>
                  <p className="text-paragraph-2xs text-text-soft mt-1.5">
                    Expires {new Date(pending.expiresAt).toLocaleString()}. Not you? Use the cancel link in the alert
                    sent to your current address, then change your password.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3.5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-email" className="text-label-xs text-text-strong">
                  New email address
                </label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={newEmail}
                  onChange={(event) => setNewEmail(event.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="email-current-password" className="text-label-xs text-text-strong">
                  Current password
                </label>
                <Input
                  id="email-current-password"
                  type="password"
                  placeholder="Your current password"
                  autoComplete="current-password"
                  value={emailPassword}
                  onChange={(event) => setEmailPassword(event.target.value)}
                  required
                />
                <p className="text-paragraph-2xs text-text-soft">
                  Confirms it is really you. We will email a link to the new address and a security alert to your
                  current one.
                </p>
              </div>

              {emailError && (
                <p className="text-paragraph-xs text-error-base flex items-start gap-1.5">
                  <RiErrorWarningLine className="mt-px size-3.5 shrink-0" />
                  <span>{emailError}</span>
                </p>
              )}
              {emailNotice && (
                <p className="text-paragraph-xs text-success-base flex items-start gap-1.5">
                  <RiCheckboxCircleFill className="mt-px size-3.5 shrink-0" />
                  <span>{emailNotice}</span>
                </p>
              )}

              <Button
                type="submit"
                size="xs"
                className="self-start"
                disabled={isEmailSubmitting || !newEmail || !emailPassword}
              >
                {isEmailSubmitting ? 'Sending…' : 'Send confirmation link'}
              </Button>
            </form>
          </div>

          <div className="border-stroke-soft mt-auto border-t px-4 py-2.5">
            <p className="text-paragraph-2xs text-text-soft">
              Your address changes only after the new inbox confirms it.
            </p>
          </div>
        </section>

        {/* ------------------------------ PASSWORD ----------------------------- */}
        <section className="border-stroke-soft bg-bg-white flex flex-col rounded-xl border">
          <div className="border-stroke-soft bg-bg-weak flex items-center gap-2 rounded-t-xl border-b px-4 py-2.5">
            <RiLockPasswordLine className="text-text-sub size-4 shrink-0" />
            <h3 className="text-label-sm text-text-strong">Password</h3>
          </div>

          <div className="flex flex-1 flex-col gap-3.5 p-4">
            <p className="text-paragraph-xs text-text-soft">Change the password used to sign in.</p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="current-password" className="text-label-xs text-text-strong">
                  Current password
                </label>
                <Input
                  id="current-password"
                  type="password"
                  placeholder="Your current password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-password" className="text-label-xs text-text-strong">
                  New password
                </label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="confirm-password" className="text-label-xs text-text-strong">
                  Confirm new password
                </label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Re-enter the new password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                />
              </div>

              {error && (
                <p className="text-paragraph-xs text-error-base flex items-start gap-1.5">
                  <RiErrorWarningLine className="mt-px size-3.5 shrink-0" />
                  <span>{error}</span>
                </p>
              )}
              {success && (
                <p className="text-paragraph-xs text-success-base flex items-start gap-1.5">
                  <RiCheckboxCircleFill className="mt-px size-3.5 shrink-0" />
                  <span>Password updated successfully.</span>
                </p>
              )}

              <Button
                type="submit"
                size="xs"
                className="self-start"
                disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword}
              >
                {isSubmitting ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          </div>

          {/*
           * Direct link. This previously read "sign out and use Forgot
           * password", which left anyone who could not recall their current
           * password with no route out of this screen.
           */}
          <div className="border-stroke-soft mt-auto border-t px-4 py-2.5">
            <p className="text-paragraph-2xs text-text-soft">
              Forgot your current password?{' '}
              <a
                href="/auth/forgot-password"
                className="text-text-sub underline underline-offset-2 hover:text-text-strong"
              >
                Send me a reset link
              </a>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}


export function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);
    const startedAt = Date.now();

    try {
      const response = await postSelfHostedAuth('/v1/auth/login', { email, password });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.data.token) {
        await withMinimumLoadingDelay(startedAt);
        setJwtToken(data.data.token);
        (window as any).Clerk = { ...((window as any).Clerk || {}), loggedIn: true };
        /*
         * One endpoint serves both. The API tags a superadmin session with
         * `scope`, and that is the only thing that decides which console loads
         * -- the route guard re-checks the claim, and the API re-checks it on
         * every request, so this is presentation, not permission.
         */
        navigate(data.data.scope === 'superadmin' ? '/superadmin' : '/');
      } else {
        throw new Error('No token received');
      }
    } catch (e: any) {
      setError(e.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-md pt-12">
      <h2 className="mb-6 text-center text-xl font-semibold">Sign In</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
            Email
          </label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="user@example.com"
            required
            className="w-full"
          />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            {/* The reset flow already existed at /auth/forgot-password but was unreachable
                from the self-hosted sign-in form. */}
            <a href="/auth/forgot-password" className="text-xs text-blue-600 hover:underline">
              Forgot password?
            </a>
          </div>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="w-full"
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button type="submit" disabled={isLoading} variant="primary" mode="filled" className="w-full">
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>
      </form>
    </div>
  );
}

export function SignUp() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validatePassword = (password: string) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[#?!@$%^&*()-]/.test(password);
    const isLengthValid = password.length >= 8 && password.length <= 64;

    if (!isLengthValid) {
      return 'Password must be between 8 and 64 characters';
    }

    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }

    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }

    if (!hasNumber) {
      return 'Password must contain at least one number';
    }

    if (!hasSpecialChar) {
      return 'Password must contain at least one special character (#?!@$%^&*()-)';
    }

    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setPasswordError(null);
    setIsSubmitted(true);

    const passwordValidationError = validatePassword(password);

    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (!organizationName.trim()) {
      setError('Organization name is required.');
      return;
    }

    setIsLoading(true);
    const startedAt = Date.now();

    try {
      const response = await postSelfHostedAuth('/v1/auth/register', {
        email,
        password,
        firstName,
        lastName: lastName || undefined,
        organizationName,
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors?.general?.messages) {
          setError(data.errors.general.messages[0]);
        } else {
          throw new Error(data.message || 'Sign up failed');
        }

        return;
      }

      if (data.data.token) {
        await withMinimumLoadingDelay(startedAt);
        setJwtToken(data.data.token);
        (window as any).Clerk = { ...((window as any).Clerk || {}), loggedIn: true };
        navigate('/');
      } else {
        throw new Error('No token received after sign up');
      }
    } catch (e: any) {
      setError(e.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md pt-12">
      <h2 className="mb-6 text-center text-xl font-semibold">Create Account</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="firstName" className="mb-1 block text-sm font-medium text-gray-700">
            First Name <span className="text-red-600">*</span>
          </label>
          <Input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
            placeholder="John"
            required
            className="w-full"
          />
        </div>
        <div>
          <label htmlFor="lastName" className="mb-1 block text-sm font-medium text-gray-700">
            Last Name
          </label>
          <Input
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
            placeholder="Doe"
            className="w-full"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
            Email <span className="text-red-600">*</span>
          </label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="user@example.com"
            required
            className="w-full"
          />
        </div>
        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
            Password <span className="text-red-600">*</span>
          </label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIsSubmitted(false);
              setPassword(e.target.value);
            }}
            placeholder="••••••••"
            required
            hasError={Boolean(isSubmitted && passwordError)}
            className="w-full"
            aria-describedby="password-constraints"
          />
          <p className="mt-1 text-xs text-gray-500" id="password-constraints">
            Min. 8 characters, include uppercase, lowercase, number, and special character.
          </p>
        </div>
        <div>
          <label htmlFor="organizationName" className="mb-1 block text-sm font-medium text-gray-700">
            Organization Name <span className="text-red-600">*</span>
          </label>
          <Input
            type="text"
            id="organizationName"
            value={organizationName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrganizationName(e.target.value)}
            placeholder="Your Company"
            required
            className="w-full"
          />
        </div>
        {error && (
          <div className="rounded-md bg-red-50 p-4" role="alert">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        <Button type="submit" disabled={isLoading} variant="primary" mode="filled" className="mt-6! w-full">
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>
        <p className="mt-4 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <span
            role="button"
            tabIndex={0}
            className="text-primary-base focus:ring-primary-base/50 cursor-pointer font-medium hover:underline focus:outline-hidden focus:ring-2"
            onClick={() => navigate('/auth/sign-in')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate('/auth/sign-in');
            }}
          >
            Sign In
          </span>
        </p>
      </form>
    </div>
  );
}

export function RedirectToSignIn({ children, redirectUrl }: { children?: any; redirectUrl?: string }) {
  if (isSelfHostedSignedIn()) {
    return <>{children}</>;
  }

  const destination =
    shouldIncludeRedirectUrl(redirectUrl) && redirectUrl
      ? `/auth/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}`
      : '/auth/sign-in';

  return <Navigate to={destination} replace />;
}

export function SignedIn({ children }: { children: any }) {
  if (!isSelfHostedSignedIn()) return null;

  return <>{children}</>;
}

export function SignedOut({ children }: { children: any }) {
  if (isSelfHostedSignedIn()) return null;

  return <>{children}</>;
}

